/**
 * Proposals and voting in the board — «Avstemninger».  MKK module 19 (C3, 2026-10-25).
 *
 * A manager writes a proposal (a title, a text, 2–6 options; «For», «Mot», «Avstår» by default), chooses open or secret
 * ballots, a simple or a two-thirds majority, an optional quorum and who may vote, and opens it. Each eligible staff
 * member casts one ballot. The vote closes at its closing time (a periodic job) or when the manager closes it, and the
 * count is stored.
 *
 * Behind the `board_voting` capability (privacy scope board_voting: who voted what is staff's personal data). Two
 * permissions (access.js):
 *   voting.vote     see the votes one is eligible in, cast one's ballot, see the results after closing
 *   voting.manage   write drafts, open, close, cancel; see every vote and, in open mode, the live count
 *
 * The rules, all here:
 *   - A vote opens only with 2–6 options and at least 2 eligible voters; the eligible list is a SNAPSHOT taken at opening.
 *   - One ballot per eligible voter, never changed.
 *   - Quorum: ballots cast (abstentions included) ≥ quorum_count, else the outcome is `no_quorum`.
 *   - The FIRST option is the proposal («For»). The option written «Avstår» is the abstention: it counts towards the
 *     quorum and never towards the majority base. Base = every ballot that is not an abstention.
 *       simple      adopted when the first option has MORE than half of the base (For > Mot with the default options)
 *       two_thirds  adopted when the first option has at least two thirds of the base (For ≥ 2/3 of For + Mot)
 *     A tie is not a majority: rejected. A base of 0 is rejected.
 *   - Secret ballots: the result is counts only; a ballot carries no voter and no time; a receipt says who voted, never
 *     what. Open ballots: who voted what is shown to the eligible voters after closing, never while the vote is open.
 *
 * MEMBER POLLS (work hub, 2026-10-31; capability `member_polls`): a vote with `scope: 'members'` is cast by the mosque's
 * verified members from the member app, not by staff. Open ballots only (a member's choice is kept with the member, and
 * shown to nobody but the managers); the eligible list is the verified, linked members when the poll opens; one ballot
 * per member, never changed; counted and closed as a board vote. A `boardMeetingItemId` ties a vote to an agenda item.
 *
 * Pure: no I/O, no clock — "now" is passed in.
 */
const { isId, oneLine, text } = require('./fields');

const MODES = ['open', 'secret'];
const MAJORITIES = ['simple', 'two_thirds'];
const STATUSES = ['draft', 'open', 'closed', 'cancelled'];
const OUTCOMES = ['adopted', 'rejected', 'no_quorum'];
const MODE_NO = { open: 'Åpen avstemning', secret: 'Hemmelig avstemning' };
const MAJORITY_NO = { simple: 'Simpelt flertall', two_thirds: 'To tredjedels flertall' };
const STATUS_NO = { draft: 'Utkast', open: 'Åpen', closed: 'Avsluttet', cancelled: 'Avlyst' };
const SCOPES = ['board', 'members'];
const SCOPE_NO = { board: 'Styret', members: 'Medlemmene' };
const OUTCOME_NO = { adopted: 'Vedtatt', rejected: 'Ikke vedtatt', no_quorum: 'Ikke beslutningsdyktig' };
const DEFAULT_OPTIONS = ['For', 'Mot', 'Avstår'];
const ABSTAIN = 'avstår';
const MAX_TITLE = 160;
const MAX_TEXT = 5000;
const MAX_OPTION = 80;
const MIN_OPTIONS = 2;
const MAX_OPTIONS = 6;
const MIN_ELIGIBLE = 2;
const MAX_ELIGIBLE = 200;
const MAX_MEETING_ITEM = 64;
/** The fields a manager may send for a draft. */
const FIELDS = ['title', 'text', 'options', 'mode', 'majority', 'quorumCount', 'eligible', 'opensAt', 'closesAt', 'meetingItemId', 'scope', 'boardMeetingItemId'];
const MEETING_ITEM_RE = /^[A-Za-z0-9_-]{1,64}$/;
const ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/;

/** An instant, as the API stores it (UTC ISO). null stays null. */
function instant(v, label) {
  if (v === undefined || v === null || v === '') return { ok: true, value: null };
  if (typeof v !== 'string' || !ISO.test(v) || Number.isNaN(Date.parse(v))) return { ok: false, message: `${label} må være et tidspunkt med dato og klokkeslett.` };
  return { ok: true, value: new Date(v).toISOString() };
}

/** The options: 2–6 distinct one-line texts. Missing → the default For / Mot / Avstår. */
function options(v) {
  if (v === undefined || v === null) return { ok: true, value: [...DEFAULT_OPTIONS] };
  if (!Array.isArray(v)) return { ok: false, message: 'Alternativene må være en liste.' };
  const out = [];
  for (const o of v) {
    const r = oneLine(o, 'et alternativ', MAX_OPTION, true);
    if (!r.ok) return { ok: false, message: r.message === 'Skriv et alternativ.' ? 'Et alternativ kan ikke være tomt.' : r.message };
    out.push(r.value);
  }
  if (out.length < MIN_OPTIONS) return { ok: false, message: `En avstemning trenger minst ${MIN_OPTIONS} alternativer.` };
  if (out.length > MAX_OPTIONS) return { ok: false, message: `En avstemning kan ha maks ${MAX_OPTIONS} alternativer.` };
  const low = out.map((o) => o.toLowerCase());
  if (new Set(low).size !== low.length) return { ok: false, message: 'To alternativer kan ikke være like.' };
  if (low[0] === ABSTAIN) return { ok: false, message: 'Det første alternativet er forslaget og kan ikke være «Avstår».' };
  return { ok: true, value: out };
}
/** The index of the abstention («Avstår», any case), or -1. */
const abstainIndex = (opts) => (opts || []).findIndex((o) => String(o).trim().toLowerCase() === ABSTAIN);

/** The eligible pick in a draft: null (everyone who holds voting.vote when it opens) or a list of staff ids. */
function eligiblePick(v) {
  if (v === undefined || v === null) return { ok: true, value: null };
  if (!Array.isArray(v) || !v.every(isId)) return { ok: false, message: 'Velg hvem som kan stemme.' };
  const ids = [...new Set(v)];
  if (ids.length > MAX_ELIGIBLE) return { ok: false, message: `Maks ${MAX_ELIGIBLE} kan stemme.` };
  return { ok: true, value: ids };
}

/**
 * A draft, as a manager writes it. input(body) → { ok, value, changed } | { ok: false, errors }. With `base` (the draft as
 * it is), only the fields sent change; `changed` names them.
 */
function input(body, base = null) {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return { ok: false, errors: { vote: 'Ugyldig avstemning.' } };
  const errors = {};
  for (const k of Object.keys(body)) if (!FIELDS.includes(k)) errors[k] = 'Ukjent felt.';
  const has = (k) => Object.prototype.hasOwnProperty.call(body, k);
  const cur = base || { title: null, text: null, options: [...DEFAULT_OPTIONS], mode: 'open', majority: 'simple', quorumCount: null, eligible: null,
    opensAt: null, closesAt: null, meetingItemId: null, scope: 'board', boardMeetingItemId: null };
  const value = { ...cur };
  const take = (k, r) => { if (!r.ok) errors[k] = r.message; else value[k] = r.value; };
  if (has('title') || !base) take('title', oneLine(body.title, 'en tittel', MAX_TITLE, true));
  if (has('text')) take('text', text(body.text, 'teksten', MAX_TEXT));
  if (has('options')) take('options', options(body.options));
  if (has('mode')) { if (!MODES.includes(body.mode)) errors.mode = 'Velg åpen eller hemmelig avstemning.'; else value.mode = body.mode; }
  if (has('majority')) { if (!MAJORITIES.includes(body.majority)) errors.majority = 'Velg simpelt flertall eller to tredjedels flertall.'; else value.majority = body.majority; }
  if (has('quorumCount')) {
    const q = body.quorumCount;
    if (q === null || q === '') value.quorumCount = null;
    else if (!Number.isInteger(q) || q < 1 || q > MAX_ELIGIBLE) errors.quorumCount = `Beslutningsdyktighet må være et helt tall fra 1 til ${MAX_ELIGIBLE}.`;
    else value.quorumCount = q;
  }
  if (has('eligible')) take('eligible', eligiblePick(body.eligible));
  if (has('opensAt')) take('opensAt', instant(body.opensAt, 'Åpner'));
  if (has('closesAt')) take('closesAt', instant(body.closesAt, 'Stenger'));
  if (has('meetingItemId')) {
    const m = body.meetingItemId;
    if (m === null || m === '') value.meetingItemId = null;
    else if (typeof m !== 'string' || !MEETING_ITEM_RE.test(m.trim())) errors.meetingItemId = 'Saken i møtet må være en id (bokstaver, tall, - og _; maks 64 tegn).';
    else value.meetingItemId = m.trim();
  }
  if (has('scope')) { if (!SCOPES.includes(body.scope)) errors.scope = 'Velg styret eller medlemmene.'; else value.scope = body.scope; }
  if (has('boardMeetingItemId')) {
    const b = body.boardMeetingItemId;
    if (b === null || b === '') value.boardMeetingItemId = null;
    else if (!isId(b)) errors.boardMeetingItemId = 'Ukjent sak.';
    else value.boardMeetingItemId = b;
  }
  // A member poll is open ballots only, and its voters are the members, never a picked list of staff.
  if (!errors.scope && value.scope === 'members') {
    if (value.mode === 'secret') errors.mode = 'En avstemning blant medlemmene er alltid åpen (ingen hemmelige stemmer).';
    if (Array.isArray(value.eligible)) errors.eligible = 'Medlemmene stemmer: alle bekreftede medlemmer med konto kan stemme.';
  }
  if (!errors.opensAt && !errors.closesAt && value.opensAt && value.closesAt && value.closesAt <= value.opensAt) errors.closesAt = 'Avstemningen må stenge etter at den åpner.';
  if (!errors.quorumCount && value.quorumCount && Array.isArray(value.eligible) && value.eligible.length && value.quorumCount > value.eligible.length) {
    errors.quorumCount = 'Beslutningsdyktighet kan ikke kreve flere stemmer enn det er stemmeberettigede.';
  }
  if (Object.keys(errors).length) return { ok: false, errors };
  const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  const changed = FIELDS.filter((k) => !base ? value[k] !== null && value[k] !== undefined : !same(value[k], cur[k]));
  return { ok: true, value, changed };
}

/**
 * Who may vote, taken when the vote opens. `staff` = [{ id, active, canVote }] (canVote: holds voting.vote). With a pick,
 * the picked ones who can still vote; without, every active one who can. Sorted, de-duplicated.
 * → { ok, value: [ids], dropped: [ids] } | { ok: false, code, message }
 */
function snapshotEligible(pick, staff) {
  const able = new Set((staff || []).filter((s) => s && s.active && s.canVote).map((s) => s.id));
  const wanted = pick ? [...new Set(pick)] : [...able];
  const value = wanted.filter((id) => able.has(id)).sort();
  const dropped = wanted.filter((id) => !able.has(id)).sort();
  if (value.length < MIN_ELIGIBLE) return { ok: false, code: 'too_few_eligible', message: `En avstemning trenger minst ${MIN_ELIGIBLE} stemmeberettigede.`, dropped };
  if (value.length > MAX_ELIGIBLE) return { ok: false, code: 'too_many_eligible', message: `Maks ${MAX_ELIGIBLE} kan stemme.` };
  return { ok: true, value, dropped };
}

const fail = (code, message) => ({ ok: false, code, message });

/**
 * Open a draft now. `vote` is the draft (options, quorumCount, closesAt, status); `eligible` the snapshot (ids).
 * → { ok, value: { status, opensAt, eligible } } | fail
 */
function open(vote, eligible, nowIso) {
  if (!vote || vote.status !== 'draft') return fail('not_draft', 'Bare et utkast kan åpnes.');
  const o = options(vote.options);
  if (!o.ok) return fail('invalid_options', o.message);
  if (!Array.isArray(eligible) || eligible.length < MIN_ELIGIBLE) return fail('too_few_eligible', `En avstemning trenger minst ${MIN_ELIGIBLE} stemmeberettigede.`);
  if (vote.quorumCount && vote.quorumCount > eligible.length) return fail('quorum_too_high', 'Beslutningsdyktighet kan ikke kreve flere stemmer enn det er stemmeberettigede.');
  if (vote.closesAt && vote.closesAt <= nowIso) return fail('closes_in_past', 'Stengetiden har allerede vært. Velg en ny før du åpner.');
  return { ok: true, value: { status: 'open', opensAt: nowIso, eligible: [...eligible] } };
}

/** May this staff member cast a ballot now? → { ok } | fail */
function canCast(vote, staffId, { hasVoted }, nowIso) {
  if (!vote || vote.status !== 'open') return fail('not_open', 'Avstemningen er ikke åpen.');
  if (vote.closesAt && vote.closesAt <= nowIso) return fail('closed', 'Avstemningen er stengt.');
  if (!Array.isArray(vote.eligible) || !vote.eligible.includes(staffId)) return fail('not_eligible', 'Du har ikke stemmerett i denne avstemningen.');
  if (hasVoted) return fail('already_voted', 'Du har allerede stemt.');
  return { ok: true };
}

/** The option a ballot names: an integer index into the vote's options. → { ok, value } | fail */
function ballot(body, vote) {
  const i = body && body.option;
  if (!Number.isInteger(i) || i < 0 || i >= (vote.options || []).length) return fail('invalid_option', 'Velg ett av alternativene.');
  return { ok: true, value: i };
}

/**
 * The count. `optionIndexes` = the option index of every ballot. → { counts, cast, base, abstained, quorumMet,
 * winner (an option index that reached the majority, or null), outcome }.
 */
function count(vote, optionIndexes) {
  const n = (vote.options || []).length;
  const counts = Array.from({ length: n }, () => 0);
  for (const i of optionIndexes || []) if (Number.isInteger(i) && i >= 0 && i < n) counts[i]++;
  const cast = counts.reduce((a, b) => a + b, 0);
  const ab = abstainIndex(vote.options);
  const abstained = ab >= 0 ? counts[ab] : 0;
  const base = cast - abstained;
  const quorumMet = !vote.quorumCount || cast >= vote.quorumCount;
  const reaches = (c) => (vote.majority === 'two_thirds' ? base > 0 && c * 3 >= base * 2 : base > 0 && c * 2 > base);
  let winner = null;
  counts.forEach((c, i) => { if (i !== ab && winner === null && reaches(c)) winner = i; });
  // Two options can both reach two thirds only when one has all of a base of 0 — excluded above; at most one wins.
  const outcome = !quorumMet ? 'no_quorum' : winner === 0 ? 'adopted' : 'rejected';
  return { counts, cast, base, abstained, quorumMet, winner: quorumMet ? winner : null, outcome };
}

/** Close an open vote: the count becomes its result. → { ok, value: { status, result } } | fail */
function close(vote, optionIndexes, nowIso) {
  if (!vote || vote.status !== 'open') return fail('not_open', 'Bare en åpen avstemning kan avsluttes.');
  const r = count(vote, optionIndexes);
  return { ok: true, value: { status: 'closed', closedAt: nowIso, result: { counts: r.counts, cast: r.cast, base: r.base, abstained: r.abstained,
    eligible: (vote.eligible || []).length, quorumCount: vote.quorumCount || null, quorumMet: r.quorumMet, winner: r.winner, outcome: r.outcome } } };
}

/** Cancel a draft or an open vote. A closed or cancelled one stays as it is. */
function cancel(vote, nowIso) {
  if (!vote || !['draft', 'open'].includes(vote.status)) return fail('not_cancellable', 'Bare et utkast eller en åpen avstemning kan avlyses.');
  return { ok: true, value: { status: 'cancelled', closedAt: nowIso } };
}

/** Is an open vote past its closing time (the periodic job closes it)? Is a draft at its opening time? */
const isDueToClose = (vote, nowIso) => Boolean(vote && vote.status === 'open' && vote.closesAt && vote.closesAt <= nowIso);
const isDueToOpen = (vote, nowIso) => Boolean(vote && vote.status === 'draft' && vote.opensAt && vote.opensAt <= nowIso);

/**
 * What one person may see of a vote. `who` = { staffId, manage }. `ballots` = [{ option, voterId }] — voterId only in
 * open mode. `receipts` = [staffId] who has voted.
 *   listed       the vote is shown to them at all (a manager: every vote; a voter: open and closed votes they are eligible in)
 *   liveCounts   the counts while open: a manager, open mode only (a secret count while open would tell who voted what
 *                next to the receipts)
 *   results      the stored result: everyone listed, once closed
 *   voters       who voted what: open mode, closed, and the person was eligible
 *   turnout      who has voted (never what): a manager, any mode
 */
function visibility(vote, who) {
  const eligible = Array.isArray(vote.eligible) && vote.eligible.includes(who.staffId);
  const listed = who.manage || (eligible && ['open', 'closed'].includes(vote.status));
  return {
    listed,
    eligible: eligible && vote.status !== 'draft',
    liveCounts: Boolean(listed && who.manage && vote.status === 'open' && vote.mode === 'open'),
    results: Boolean(listed && vote.status === 'closed'),
    voters: Boolean(eligible && vote.status === 'closed' && vote.mode === 'open'),
    turnout: Boolean(who.manage && vote.status !== 'draft'),
  };
}

/**
 * Who may vote in a member poll, taken when it opens: every verified member with a linked account.
 * `members` = [{ id }] (the API reads them). → as snapshotEligible.
 */
function snapshotMembers(members) {
  const ids = [...new Set((members || []).map((m) => m && m.id).filter(isId))].sort();
  if (ids.length < MIN_ELIGIBLE) return { ok: false, code: 'too_few_eligible', message: `En avstemning trenger minst ${MIN_ELIGIBLE} stemmeberettigede.`, dropped: [] };
  return { ok: true, value: ids.slice(0, 100000), dropped: [] };
}

/**
 * What one MEMBER may see of a member poll (`memberId`: their member id). Listed while open or closed and the member is
 * on the eligible list; the result once closed; never who voted what (that is the managers').
 */
function memberVisibility(vote, memberId) {
  const eligible = vote.scope === 'members' && Array.isArray(vote.eligible) && vote.eligible.includes(memberId);
  return { listed: eligible && ['open', 'closed'].includes(vote.status), eligible, results: eligible && vote.status === 'closed' };
}

/** The audit's view of a vote: its state and shape, never its title, its text, its options or anyone's choice. */
function auditView(v) {
  const out = { vote_status: v.status, vote_mode: v.mode, vote_majority: v.majority, vote_scope: v.scope || 'board' };
  if (Array.isArray(v.eligible)) out.vote_eligible_count = v.eligible.length;
  if (v.result && v.result.outcome) { out.vote_outcome = v.result.outcome; out.vote_ballots = v.result.cast; }
  return out;
}

/** «For 5 · Mot 2 · Avstår 1». */
const countsText = (opts, counts) => (opts || []).map((o, i) => `${o} ${(counts || [])[i] || 0}`).join(' · ');

/** The label of the majority rule in words a board member reads. */
function ruleText(vote) {
  const q = vote.quorumCount ? ` Minst ${vote.quorumCount} må stemme.` : '';
  return vote.majority === 'two_thirds' ? `«${vote.options[0]}» trenger minst to tredjedeler av stemmene, «Avstår» ikke medregnet.${q}`
    : `«${vote.options[0]}» trenger flere enn halvparten av stemmene, «Avstår» ikke medregnet. Står det likt, er forslaget ikke vedtatt.${q}`;
}

module.exports = {
  MODES, MAJORITIES, STATUSES, OUTCOMES, SCOPES, MODE_NO, MAJORITY_NO, STATUS_NO, OUTCOME_NO, SCOPE_NO, DEFAULT_OPTIONS, FIELDS,
  MAX_TITLE, MAX_TEXT, MAX_OPTION, MIN_OPTIONS, MAX_OPTIONS, MIN_ELIGIBLE, MAX_ELIGIBLE, MAX_MEETING_ITEM,
  isId, input, options, abstainIndex, eligiblePick, snapshotEligible, snapshotMembers, open, canCast, ballot, count, close, cancel,
  isDueToClose, isDueToOpen, visibility, memberVisibility, auditView, countsText, ruleText,
};
